/**
 * 設定 → 使う機能（#142 段3、docs/52 §3）。会社ごとのスイッチ（{trade}・場所・品目マスタ・{takeoff}・{vendorQuote}の取込・{vendor}への見積依頼・地図・受け取った書類）。
 *   受け取った書類（../eigyouryoku/docs/13 §4.4）は節「書類」（見積の行と 外注・地図 のあいだ）
 *   押した瞬間に保存（PUT /company/features。変えられるのは管理者だけ）。OFF にするとボタン・欄・メニューが隠れるだけで、データは残る
 *   （紙・金額も変わらない。ON に戻すと元どおり）。既定は製品（業種）で決まり、変えた分だけ DB に残る。
 *   判定は core/uses.ts 1か所。名前は言葉を通す（{trade} など）
 * #156（docs/65 §4、Figma「設定の残り（10/9）」⑱）で iOS 風に：節「見積の行」（{trade}／場所／品目マスタ／{takeoff}）・
 *   「外注・地図」（営業力は言葉どおり「{vendor}・地図」。{vendorQuote}の取込／{vendor}への見積依頼／地図）。並びは USE_KEYS の順。
 *   行は #154 のスイッチの行（右に「使う／使わない」の字＋トグル。S-00009 で「オン／オフ」から）。行ごとの「？」と「既定」の字はやめ、節の見出しの「？」に機能ごとの説明と既定を並べた
 */
import { FEATURES_MSG, REST_MSG, SETTINGS_MSG, USE_INFO, USE_KEYS, type FeatureSettings, type UseKey } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupList, GroupSection, GroupSwitchRow } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";

const KEY = ["company-features"] as const;
/** 節の分け方（並びは USE_KEYS の順） */
const LINE_KEYS: readonly UseKey[] = ["trades", "places", "items", "takeoff", "reducedTax"]; // T2：軽減税率・税区分は「見積の行」の節（明細の列）
/** 節「書類」（受け取った書類、../eigyouryoku/docs/13 §4.4）。「外注・地図」の前に置く（使わないときの注意の文は最後の節の下のまま） */
const DOC_KEYS: readonly UseKey[] = ["receivedDocs"];
/** 節「{project}」（#289：{ongoingProject}）。「書類」の次 */
const PROJECT_KEYS: readonly UseKey[] = ["ongoingProjects"];

export function SettingsFeaturesPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const eigyo = me.data?.tenant.product === "eigyo";
  const q = useQuery({ queryKey: KEY, queryFn: () => api<FeatureSettings>("GET", "/company/features") });
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(FEATURES_MSG.title);
  const cls = useRestPageClass("featpage");
  const save = useMutation({
    mutationFn: (v: { key: UseKey; on: boolean }) => api<FeatureSettings>("PUT", "/company/features", v),
    onSuccess: (v, sent) => {
      qc.setQueryData(KEY, v);
      // メニュー・見積の画面などは Me.tenant.uses を見る＝取り直す
      void qc.invalidateQueries({ queryKey: ME_KEY });
      toast(FEATURES_MSG.saved.replace("{name}", t(USE_INFO[sent.key].title)).replace("{state}", sent.on ? FEATURES_MSG.on : FEATURES_MSG.off));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const data = q.data;
  const on = (k: UseKey) => (save.isPending && save.variables?.key === k ? save.variables.on : data.uses.includes(k));
  const lineKeys = USE_KEYS.filter((k) => LINE_KEYS.includes(k));
  const docKeys = USE_KEYS.filter((k) => DOC_KEYS.includes(k));
  const projectKeys = USE_KEYS.filter((k) => PROJECT_KEYS.includes(k));
  const vendorKeys = USE_KEYS.filter((k) => !LINE_KEYS.includes(k) && !DOC_KEYS.includes(k) && !PROJECT_KEYS.includes(k));
  /** 節の見出しの「？」：機能ごとの説明と既定（前は行ごとの「？」と「既定」の字） */
  const helpOf = (keys: readonly UseKey[], testId: string) => (
    <Help
      testId={testId}
      text={
        <>
          {keys.map((k) => (
            <span key={k} className="helpp" data-testid={`help-feature-${k}`}>
              <b>{t(USE_INFO[k].title)}</b>
              {`　${t(USE_INFO[k].help)}${data.defaults.includes(k) ? REST_MSG.featuresDefaultOn : REST_MSG.featuresDefaultOff}`}
            </span>
          ))}
          <span className="helpp">{FEATURES_MSG.help}</span>
        </>
      }
    />
  );
  const rows = (keys: readonly UseKey[]) =>
    keys.map((k) => {
      const name = t(USE_INFO[k].title);
      return <GroupSwitchRow key={k} label={name} name={name} checked={on(k)} disabled={!admin || save.isPending} onChange={(v) => save.mutate({ key: k, on: v })} testId={`sw-feature-${k}`} rowTestId={`feature-${k}`} />;
    });

  return (
    <div className={cls} data-testid="features">
      <SettingsHead back={back} title={FEATURES_MSG.title} />
      {!admin ? (
        <p className="est-note" id="featuresMemberNote">
          {SETTINGS_MSG.memberNote}
        </p>
      ) : null}
      <GroupSection title={REST_MSG.featuresSectionLines} help={helpOf(lineKeys, "help-features-lines")} data-testid="features-sec-lines">
        <GroupList>{rows(lineKeys)}</GroupList>
      </GroupSection>
      <GroupSection title={REST_MSG.featuresSectionDocs} help={helpOf(docKeys, "help-features-docs")} data-testid="features-sec-docs">
        <GroupList>{rows(docKeys)}</GroupList>
      </GroupSection>
      <GroupSection title={t(REST_MSG.featuresSectionProjects)} help={helpOf(projectKeys, "help-features-projects")} data-testid="features-sec-projects">
        <GroupList>{rows(projectKeys)}</GroupList>
      </GroupSection>
      <GroupSection title={eigyo ? t(REST_MSG.featuresSectionVendorEigyo) : REST_MSG.featuresSectionVendor} help={helpOf(vendorKeys, "help-features-vendor")} note={REST_MSG.featuresNote} data-testid="features-sec-vendor">
        <GroupList>{rows(vendorKeys)}</GroupList>
      </GroupSection>
      {node}
    </div>
  );
}
